import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { EmailDetails } from 'features/sendouts/components/forms';
import { InterviewSchedule } from 'features/sendouts/components/forms/InterviewSchedule';
import { ATTACHMENTS_CATEGORIES } from 'features/sendouts/constants';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import { CandidateFileCategoriesWithSendout } from 'UI/components/organisms/sendouts/utils';
import { Endpoints } from 'UI/constants/endpoints';

import { AccountableRecruiterSelector, Attachments, CandidateDetailsFields } from '../../../shared';

import { FORM_FIELDS_MAP, SENDOUT_TEMPLATE_ID } from './SendoutForm.constants';
import { NumberedFormWrapper } from './SendoutForm.styled';
import { ATTACHMENT_FIELD_KEYS, buildAttachmentFieldValues } from './SendoutForm.utils';

export const SendoutForm = ({
  candidateAttachments = [],
  candidateHydratedId = null,
  candidateInfo = null,
  canSkipHiringAuthorityEmail = false,
  duplicateInfo = { isDuplicate: false },
  hydrateCandidate,
  isCandidateLocked = false,
  isHydratingCandidate = false,
  jobOrder = null,
  recruiterOptions = [],
  showAttachments = true,
  showEmailDetails = true,
  showDuplicateWarning = true,
  onViewDuplicates = () => {}
}) => {
  const { errors, getValues, setValue, watch } = useFormContext();

  const formValues = watch([
    FORM_FIELDS_MAP.AccountableRecruiter.key,
    FORM_FIELDS_MAP.Candidate.key,
    FORM_FIELDS_MAP.CandidateRecruiter.key
  ]);

  const accountableRecruiter = formValues[FORM_FIELDS_MAP.AccountableRecruiter.key];
  const candidateValue = formValues[FORM_FIELDS_MAP.Candidate.key];
  const candidateId = candidateValue?.id;
  const candidateRecruiterValue = formValues[FORM_FIELDS_MAP.CandidateRecruiter.key];

  const areCandidateAttachmentsReady =
    !candidateId || (!isHydratingCandidate && candidateHydratedId === candidateId);

  const syncAttachmentFields = useCallback(
    attachments => {
      const attachmentFieldValues = buildAttachmentFieldValues(attachments);

      ATTACHMENT_FIELD_KEYS.forEach(fieldKey => {
        setValue(fieldKey, attachmentFieldValues[fieldKey], false);
      });
    },
    [setValue]
  );

  const handleOnSelectCandidate = useCallback(
    async candidate => {
      const requestedCandidateId = candidate?.id;
      const { attachments, resolvedRecruiter } = await hydrateCandidate({
        candidateId: requestedCandidateId
      });

      const currentCandidate = getValues(FORM_FIELDS_MAP.Candidate.key);
      const isStaleCandidateSelection = currentCandidate?.id !== requestedCandidateId;
      if (isStaleCandidateSelection) {
        return;
      }

      syncAttachmentFields(attachments);
      setValue(FORM_FIELDS_MAP.CandidateRecruiter.key, resolvedRecruiter, false);
    },
    [getValues, hydrateCandidate, setValue, syncAttachmentFields]
  );

  return (
    <Grid container>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <Box display="flex" flexDirection="column" mb={2}>
            <Typography variant="subtitle1">
              {strings.sendoutsV2.form.sections.accountableSelector.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {strings.sendoutsV2.form.sections.accountableSelector.subtitle}
            </Typography>
          </Box>
          <AccountableRecruiterSelector jobOrderId={jobOrder?.id} />
        </Grid>

        <When condition={accountableRecruiter}>
          <NumberedFormWrapper item xs={12}>
            <NumberedForm>
              <NumberedForm.Item title={strings.sendoutsV2.form.sections.candidateDetails.title}>
                <CandidateDetailsFields
                  isCandidateLocked={isCandidateLocked}
                  isHydratingCandidate={isHydratingCandidate}
                  onSelectCandidate={handleOnSelectCandidate}
                  recruiterOptions={recruiterOptions}
                  showEstimatedFeeAmount
                />
              </NumberedForm.Item>
              <When condition={showDuplicateWarning && duplicateInfo.isDuplicate}>
                <NumberedForm.Container>
                  <Alert
                    severity="warning"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        onClick={() => onViewDuplicates({ candidate: candidateValue })}
                      >
                        View
                      </Button>
                    }
                  >
                    {strings.sendoutsV2.form.sections.candidateDetails.alerts.duplicateCandidate}
                  </Alert>
                </NumberedForm.Container>
              </When>
              <When
                condition={
                  showAttachments &&
                  candidateValue &&
                  candidateRecruiterValue &&
                  areCandidateAttachmentsReady
                }
              >
                <NumberedForm.Item title={strings.sendoutsV2.form.sections.attachments.title}>
                  <Attachments
                    key={`${candidateId}-${candidateAttachments.length}`}
                    categories={[
                      ATTACHMENTS_CATEGORIES.resume,
                      ATTACHMENTS_CATEGORIES.portfolio,
                      ATTACHMENTS_CATEGORIES.other
                    ]}
                    categoryIdMap={CandidateFileCategoriesWithSendout}
                    endpoint={Endpoints.Files}
                    files={candidateAttachments}
                    maxNumberOfFiles={Infinity}
                  />
                  <When condition={!!errors[FORM_FIELDS_MAP.Attachments.key]}>
                    <Box mt={2}>
                      <Alert severity="error">
                        {errors[FORM_FIELDS_MAP.Attachments.key]?.message}
                      </Alert>
                    </Box>
                  </When>
                </NumberedForm.Item>
              </When>

              <When condition={candidateValue && candidateRecruiterValue}>
                <NumberedForm.Item title={strings.sendoutsV2.form.sections.interviewDetails.title}>
                  <InterviewSchedule />
                </NumberedForm.Item>
              </When>
              <When condition={candidateValue && candidateRecruiterValue && showEmailDetails}>
                <NumberedForm.Item title={strings.sendoutsV2.form.sections.emailDetails.title}>
                  <EmailDetails
                    jobOrder={jobOrder}
                    candidateInfo={candidateInfo}
                    templateId={SENDOUT_TEMPLATE_ID}
                    canDisableEmailToHiringAuthority={canSkipHiringAuthorityEmail}
                  />
                </NumberedForm.Item>
              </When>
            </NumberedForm>
          </NumberedFormWrapper>
        </When>
      </Grid>
    </Grid>
  );
};
